import { BoardColumns, boardEntityName, BoardType } from '@/backend/features/board-files/shared';
import { BoardFilesList } from '@/ui/features/board-files/components/BoardFilesList';
import { useHandleAddBoard } from '@/ui/features/board-files/hooks/useHandleAddBoard';
import { useTranslation } from '@/ui/hooks';
import { HtmlOutput } from '@juvare/common-form-ui/HtmlOutput';
import { ChipLight } from '@juvare/common-web-ui/ChipLight';
import { PageError } from '@juvare/common-web-ui/PageError';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useStorageList } from '@juvare/platform-sdk-react/ui/hooks';
import { getUserState } from '@juvare/platform-sdk-react/ui/stores';
import { Row } from '@juvare/platform-shared/features/storage';
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';

export const BoardDownloadsPage: React.FC = () => {
    const { t } = useTranslation();

    const { permissions } = useSelector(getUserState);
    const allowUpload = permissions.includes('upload');

    const { downloadsPageContent1, downloadsPageContent2, downloadsPageDocumentationLink } = useSelector(getSettings);

    const { data, isLoading, error, mutate } = useStorageList<BoardColumns>(boardEntityName, {
        orderBy: [{ column: 'industry' }, { column: 'board' }],
    });

    const handleAddGenericBoard = useHandleAddBoard('generic', mutate);
    const handleAddIndustryBoard = useHandleAddBoard('industry', mutate);

    if (error) {
        return <PageError error={error} />;
    }

    const genericBoards = (data?.rows ?? []).filter((row) => row.columns.type);
    const industryBoards = (data?.rows ?? []).filter((row) => row.columns.industry);

    const groupedIndustryBoards: Record<string, Row<BoardColumns>[]> = {};
    for (const industryBoard of industryBoards) {
        if (industryBoard.columns.industry) {
            let group = groupedIndustryBoards[industryBoard.columns.industry];
            if (!group) {
                group = [];
                groupedIndustryBoards[industryBoard.columns.industry] = group;
            }
            group.push(industryBoard);
        }
    }

    return (
        <Box p={4}>
            {data && (
                <Grid container columnSpacing={4} rowSpacing={2}>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Typography variant='h4' component='h1'>
                                {t('appTitle')}
                            </Typography>
                            <Box sx={{ '& p': { typography: 'body2' } }}>
                                <HtmlOutput html={downloadsPageContent1.content.html} />
                            </Box>
                            <Box>
                                <Button component='a' variant='contained' size='small' href={downloadsPageDocumentationLink}>
                                    Return to Documentation
                                </Button>
                            </Box>
                            <Box sx={{ '& p': { typography: 'body2' } }}>
                                <HtmlOutput html={downloadsPageContent2.content.html} />
                            </Box>
                            {isLoading && (
                                <Box>
                                    <CircularProgress />
                                </Box>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Typography variant='h4' component='h2'>
                                {t('genericBoardsTitle')}
                            </Typography>
                            <TableContainer component={Paper}>
                                <Table size='small' sx={{ '& td': { verticalAlign: 'top' } }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('board')}</TableCell>
                                            <TableCell>{t('type')}</TableCell>
                                            <TableCell>{t('downloadLink')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {genericBoards.map((row) => (
                                            <TableRow key={row.id}>
                                                <TableCell>{row.columns.board}</TableCell>
                                                <TableCell>
                                                    <ChipLight
                                                        variant='outlined'
                                                        color={
                                                            (row.columns.type as BoardType) === 'standard'
                                                                ? 'primary'
                                                                : (row.columns.type as BoardType) === 'addon'
                                                                  ? 'success'
                                                                  : (row.columns.type as BoardType) === 'premium'
                                                                    ? 'warning'
                                                                    : 'default'
                                                        }
                                                        size='small'
                                                        label={t(`boardTypeEnum.${row.columns.type as BoardType}`)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <BoardFilesList row={row} reload={mutate} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {allowUpload && (
                                            <TableRow>
                                                <TableCell colSpan={3}>
                                                    <Button variant='contained' size='small' onClick={handleAddGenericBoard}>
                                                        {t('addBoard')}
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Typography variant='h4' component='h2'>
                                {t('industryBoardsTitle')}
                            </Typography>
                            <TableContainer component={Paper}>
                                <Table size='small' sx={{ '& td': { verticalAlign: 'top' } }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('industry')}</TableCell>
                                            <TableCell>{t('board')}</TableCell>
                                            <TableCell>{t('downloadLink')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(groupedIndustryBoards).map(([industry, boards]) => (
                                            <React.Fragment key={industry}>
                                                {boards.map((board, index) => (
                                                    <TableRow key={board.id}>
                                                        {index === 0 && <TableCell rowSpan={boards.length}>{industry}</TableCell>}
                                                        <TableCell>{board.columns.board}</TableCell>
                                                        <TableCell>
                                                            <BoardFilesList row={board} reload={mutate} />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                        {allowUpload && (
                                            <TableRow>
                                                <TableCell colSpan={3}>
                                                    <Button variant='contained' size='small' onClick={handleAddIndustryBoard}>
                                                        {t('addBoard')}
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};
